import { AlertCircle, ArrowUpRight, ChevronLeft, Info, ListOrdered, RefreshCw, Send } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { formatSendMoneyWithCode } from "@/modules/dashboard/dispersions/components/WalletSend/formatSendMoney";
import type { RuntimeWallet } from "@/modules/dashboard/wallets/types/payments-wallets.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import {
  EMPTY_MASKED_AMOUNT,
  type MaskedAmount,
  maskDecimalString,
  maskForCurrency,
} from "@/shared/utils/amountMask";
import { compareDecimalStrings } from "@/shared/utils/decimal";

import { useWalletSwapQuote } from "../../hooks/useWalletSwapQuote";
import { useWalletSwapSubmit } from "../../hooks/useWalletSwapSubmit";
import {
  type SwapAnchor,
  type SwapBreakdown,
  swapBreakdown,
  swapDestinations,
  swapOrigins,
} from "../../utils/walletSwap";
import { SwapAmountFields } from "./SwapAmountFields";
import { SwapReview } from "./SwapReview";
import { SwapRouteStrip } from "./SwapRouteStrip";
import { SwapSuccess } from "./SwapSuccess";
import { SwapTransit } from "./SwapTransit";

export interface WalletSwapPanelProps {
  /** The wallet whose card opened the panel: where the money leaves from, until the user picks another. */
  wallet: RuntimeWallet;
  /** Every wallet of the organization: origins are the active ones, destinations the others. */
  wallets: RuntimeWallet[];
  open: boolean;
  onClose: () => void;
  /** The conversion went through: both balances moved. */
  onSwapped?: (originId: string, destinationId: string) => void;
  /** "Send it from Send instead": closes this panel and opens the send one on that wallet. */
  onOpenSend?: (walletId: string) => void;
}

type View = "form" | "review";

/** The first wallet money may land in from `origin`, keeping `preferred` when it still qualifies. */
function pickDestination(
  wallets: readonly RuntimeWallet[],
  origin: RuntimeWallet,
  preferred: string | null,
): string | null {
  const options = swapDestinations(wallets, origin).filter((option) => !option.disabled);
  return (options.find((option) => option.wallet.id === preferred) ?? options[0])?.wallet.id ?? null;
}

/**
 * Converts money between two of the organization's own wallets, in a drawer opened from a wallet card. Two
 * linked amounts — what leaves and what lands — where the one typed last is the one the price is fixed on,
 * and the other is what the quote answers. Instant on the backend, so the transit is shown for a moment and
 * the success screen reads both balances off the swap's own answer.
 */
export function WalletSwapPanel({
  wallet,
  wallets,
  open,
  onClose,
  onSwapped,
  onOpenSend,
}: WalletSwapPanelProps) {
  const { t, activeLanguage } = useTranslations("swaps");
  const { can } = useAuth();
  const navigate = useNavigate();
  const canWrite = can("payments:write");

  const origins = useMemo(() => {
    const active = swapOrigins(wallets);
    return active.some((item) => item.id === wallet.id) ? active : [wallet, ...active];
  }, [wallets, wallet]);

  const [originId, setOriginId] = useState(wallet.id);
  const origin = origins.find((item) => item.id === originId) ?? wallet;
  const destinations = useMemo(() => swapDestinations(wallets, origin), [wallets, origin]);
  const [destinationId, setDestinationId] = useState<string | null>(() =>
    pickDestination(wallets, wallet, null),
  );
  const destination =
    destinations.find((option) => option.wallet.id === destinationId && !option.disabled)?.wallet ?? null;

  const [anchor, setAnchor] = useState<SwapAnchor>("send");
  const [sendInput, setSendInput] = useState<MaskedAmount>(EMPTY_MASKED_AMOUNT);
  const [receiveInput, setReceiveInput] = useState<MaskedAmount>(EMPTY_MASKED_AMOUNT);
  const [view, setView] = useState<View>("form");
  const [reviewed, setReviewed] = useState<SwapBreakdown | null>(null);

  const from = origin.currency;
  const to = destination?.currency ?? null;
  const typed = anchor === "send" ? sendInput : receiveInput;
  const typedAmount = typed.value != null && typed.value > 0 ? String(typed.value) : null;

  const quoteState = useWalletSwapQuote({
    originCurrency: from,
    destinationCurrency: to ?? "",
    amount: typedAmount,
    anchor,
    enabled: canWrite && open && !!to,
  });
  const { quote, lastQuote } = quoteState;
  // The figure on the side not typed: the price on screen, or the last one dimmed while the next arrives.
  const shownQuote = typedAmount ? (quote ?? (quoteState.loading ? lastQuote : null)) : null;
  // While a refreshed price is on its way the review keeps showing the last one; the button waits for it.
  const reviewQuote = quote ?? lastQuote;

  const derivedValue = (side: SwapAnchor): MaskedAmount => {
    if (!shownQuote) return EMPTY_MASKED_AMOUNT;
    const breakdown = swapBreakdown(shownQuote);
    return side === "send"
      ? maskDecimalString(breakdown.debit, maskForCurrency(from))
      : maskDecimalString(breakdown.credit, maskForCurrency(shownQuote.currency.to));
  };
  const sendShown = anchor === "send" ? sendInput : derivedValue("send");
  const receiveShown = anchor === "receive" ? receiveInput : derivedValue("receive");

  // What leaves the wallet: the quote's exact debit, or the typed amount while it is the debit itself.
  const debit = quote ? swapBreakdown(quote).debit : anchor === "send" ? typedAmount : null;
  const insufficient = !!debit && compareDecimalStrings(debit, origin.balance_available) > 0;
  const sendError =
    insufficient && debit
      ? t("convert_insufficient", {
          balance: formatSendMoneyWithCode(origin.balance_available, from, activeLanguage),
          debit: formatSendMoneyWithCode(debit, from, activeLanguage),
        })
      : null;
  const quoteError =
    typedAmount && !quoteState.loading
      ? quoteState.unavailable && to
        ? t("convert_pair_unavailable", { from, to })
        : quoteState.error
          ? quoteState.retryable
            ? t("convert_quote_error")
            : quoteState.error
          : null
      : null;

  const submit = useWalletSwapSubmit({
    onQuoteExpired: quoteState.refresh,
    onQuoteSpent: quoteState.refresh,
  });
  const inResult = submit.phase !== "idle";

  const clearAmounts = () => {
    setSendInput(EMPTY_MASKED_AMOUNT);
    setReceiveInput(EMPTY_MASKED_AMOUNT);
    setAnchor("send");
  };

  const handleOriginChange = (id: string) => {
    const next = origins.find((item) => item.id === id);
    if (!next || next.id === origin.id) return;
    const nextDestination = pickDestination(wallets, next, destinationId);
    // An amount typed in a currency that is no longer on its side would mean something else: start over.
    if (next.currency !== from && anchor === "send") clearAmounts();
    if (nextDestination !== destinationId && anchor === "receive") clearAmounts();
    setOriginId(next.id);
    setDestinationId(nextDestination);
  };

  const handleDestinationChange = (id: string) => {
    if (id === destinationId) return;
    if (anchor === "receive") clearAmounts();
    setDestinationId(id);
  };

  // Both ends must work the other way round: a USD origin cannot be a destination, since the home hides it.
  const canFlip =
    !!destination &&
    destination.status === "active" &&
    swapDestinations(wallets, destination).some(
      (option) => option.wallet.id === origin.id && !option.disabled,
    );
  // The amount stays in its currency, so it moves to the other field and the price is fixed on that side.
  const handleFlip = () => {
    if (!destination) return;
    const previousOrigin = origin.id;
    setOriginId(destination.id);
    setDestinationId(previousOrigin);
    setAnchor(anchor === "send" ? "receive" : "send");
    setSendInput(receiveInput);
    setReceiveInput(sendInput);
  };

  const handleSendChange = (next: MaskedAmount) => {
    setAnchor("send");
    setSendInput(next);
  };
  const handleReceiveChange = (next: MaskedAmount) => {
    setAnchor("receive");
    setReceiveInput(next);
  };

  const canReview = !!quote && !quoteState.loading && !insufficient && !!destination;

  const openReview = () => {
    if (!quote) return;
    setReviewed(swapBreakdown(quote));
    setView("review");
  };

  const handleConvert = async () => {
    if (!quote || !destination) return;
    const swap = await submit.submit(quote);
    if (swap) onSwapped?.(swap.wallets.origin.id, swap.wallets.destination.id);
  };

  const startOver = () => {
    submit.reset();
    clearAmounts();
    setReviewed(null);
    setView("form");
    quoteState.refresh();
  };

  const backToForm = () => {
    submit.setPhase("idle");
    setView("form");
  };

  const viewMovements = (walletId: string) => {
    onClose();
    navigate(`/app/wallets/${walletId}/movements`);
  };

  const handleOpenChange = (next: boolean) => {
    if (next || submit.phase === "processing") return;
    onClose();
  };

  const body = (() => {
    if (!canWrite) return <ColocaAlert variant="secondary" icon={Info} text={t("convert_read_only")} />;
    if (submit.phase === "processing" && reviewQuote && to) {
      const breakdown = swapBreakdown(reviewQuote);
      return (
        <div className="flex flex-col items-center gap-4 pt-6 text-center" aria-live="polite">
          <SwapTransit
            className="w-full"
            from={from}
            to={to}
            debit={breakdown.debit}
            credit={breakdown.credit}
          />
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-foreground">{t("convert_processing", { from, to })}</h2>
            <p className="text-sm text-muted-foreground">{t("convert_processing_hint")}</p>
          </div>
        </div>
      );
    }
    if (submit.phase === "success" && submit.swap) return <SwapSuccess swap={submit.swap} />;
    if (submit.phase === "error") {
      const spent = submit.errorKind !== "generic";
      return (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          heading={t("convert_error_title")}
          text={spent ? t("convert_error_consumed") : (submit.errorMessage ?? t("convert_error_generic"))}
        />
      );
    }
    if (view === "review" && reviewQuote) return <SwapReview quote={reviewQuote} reviewed={reviewed} />;
    return (
      <div className="space-y-6">
        <SwapRouteStrip
          origins={origins}
          origin={origin}
          onOriginChange={handleOriginChange}
          destinations={destinations}
          destination={destination}
          onDestinationChange={handleDestinationChange}
          onFlip={handleFlip}
          canFlip={canFlip}
        />
        {to && (
          <SwapAmountFields
            originCurrency={from}
            destinationCurrency={to}
            anchor={anchor}
            send={sendShown}
            receive={receiveShown}
            onSendChange={handleSendChange}
            onReceiveChange={handleReceiveChange}
            quote={shownQuote}
            loading={quoteState.loading}
            sendError={sendError}
            quoteError={quoteError}
            onRetry={quoteState.refresh}
          />
        )}
        {onOpenSend && (
          <aside className="flex items-start gap-3 rounded-xl border border-dashed border-border px-4 py-3">
            <Send className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-medium text-foreground">{t("convert_send_tip_title")}</p>
              <p className="text-xs text-muted-foreground">{t("convert_send_tip_body")}</p>
              <ColocaButton
                type="button"
                variant="link"
                size="sm"
                icon={ArrowUpRight}
                onClick={() => onOpenSend(origin.id)}
                className="h-auto px-0"
              >
                {t("convert_send_tip_action")}
              </ColocaButton>
            </div>
          </aside>
        )}
      </div>
    );
  })();

  const footer = (() => {
    if (!canWrite) {
      return (
        <ColocaButton variant="ghost" onClick={onClose}>
          {t("convert_close")}
        </ColocaButton>
      );
    }
    if (submit.phase === "processing") return undefined;
    if (submit.phase === "success" && submit.swap) {
      const destinationWalletId = submit.swap.wallets.destination.id;
      return (
        <>
          <ColocaButton variant="ghost" icon={RefreshCw} onClick={startOver}>
            {t("convert_again")}
          </ColocaButton>
          <ColocaButton
            variant="secondary"
            icon={ListOrdered}
            onClick={() => viewMovements(destinationWalletId)}
          >
            {t("convert_view_movements")}
          </ColocaButton>
          <ColocaButton variant="primary" onClick={onClose}>
            {t("convert_done")}
          </ColocaButton>
        </>
      );
    }
    if (submit.phase === "error") {
      if (submit.errorKind !== "generic") {
        return (
          <>
            <ColocaButton variant="ghost" onClick={onClose}>
              {t("convert_close")}
            </ColocaButton>
            <ColocaButton variant="primary" icon={ListOrdered} onClick={() => viewMovements(origin.id)}>
              {t("convert_view_movements")}
            </ColocaButton>
          </>
        );
      }
      return (
        <ColocaButton variant="primary" icon={ChevronLeft} onClick={backToForm}>
          {t("convert_error_back")}
        </ColocaButton>
      );
    }
    if (view === "review" && reviewQuote) {
      return (
        <>
          <ColocaButton variant="ghost" icon={ChevronLeft} onClick={() => setView("form")}>
            {t("convert_review_edit")}
          </ColocaButton>
          <ColocaButton
            variant="primary"
            disabled={!quote || quoteState.loading || insufficient}
            onClick={() => void handleConvert()}
          >
            {t("convert_confirm", {
              amount: formatSendMoneyWithCode(swapBreakdown(reviewQuote).debit, from, activeLanguage),
            })}
          </ColocaButton>
        </>
      );
    }
    return (
      <>
        <ColocaButton variant="ghost" onClick={onClose}>
          {t("convert_cancel")}
        </ColocaButton>
        <ColocaButton variant="primary" disabled={!canReview} onClick={openReview}>
          {t("convert_review")}
        </ColocaButton>
      </>
    );
  })();

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={handleOpenChange}
      size="lg"
      pinLastChild={false}
      showCloseButton={submit.phase !== "processing"}
      title={view === "review" && !inResult ? t("convert_review_title") : t("convert_title")}
      description={t("convert_subtitle")}
      footer={footer}
    >
      {body}
    </ColocaDrawer>
  );
}
